import { useState, useCallback, useEffect, useRef } from "react";
import axios from "../services/axios";

interface UseLiveDataOptions {
  deviceId: string | undefined;
  enabled: boolean;
  frequency?: number;
  onDataUpdate?: (data: any) => void;
  autoDisableAfterInactivity?: number;
}

export const useLiveData = ({
  deviceId,
  enabled,
  frequency = 10000, 
  onDataUpdate,
  autoDisableAfterInactivity = 120000,
}: UseLiveDataOptions) => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastUpdateRef = useRef<number | null>(null);
  const inactivityTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
      inactivityTimeoutRef.current = null;
    }
  }, []);

  const fetchLiveData = useCallback(async () => {
    if (!deviceId || !enabled) return;

    try {
      setLoading(true);
      const response = await axios.get(`/api-v1/device/${deviceId}/live-data/data/`);
      const newData = response.data;
      setData(newData);
      setError(null);
      lastUpdateRef.current = Date.now();

      if (onDataUpdate) {
        onDataUpdate(newData);
      }

      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current);
      }

      // Auto-disable after inactivity
      inactivityTimeoutRef.current = setTimeout(() => {
        if (enabled) {
          // TODO: Implement auto-disable logic - stop polling and clear data after inactivity
          // Note: This doesn't stop the session, just stops polling
          // The session will continue until it expires or is manually stopped
        }
      }, autoDisableAfterInactivity);

      setLoading(false);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || "Failed to fetch live data");
      setLoading(false);
    }
  }, [deviceId, enabled, onDataUpdate, autoDisableAfterInactivity]);

  useEffect(() => {
    setData(null);
    lastUpdateRef.current = null;
  }, [deviceId]);

  useEffect(() => {
    stopPolling();

    if (enabled && deviceId) {
      fetchLiveData();

      const interval = setInterval(() => {
        fetchLiveData();
      }, frequency);

      pollingIntervalRef.current = interval;

      return () => {
        clearInterval(interval);
      };
    }
  }, [enabled, deviceId, frequency, fetchLiveData, stopPolling]);

  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  return {
    data,
    loading,
    error,
    refetch: fetchLiveData,
    clearError: () => setError(null),
  };
};

